import { type ApiRequest, callApi, type ApiResponse, deleteAPI } from '@/api/tenantClient';

// Types
export interface TableViewConfig {
  columnFilters: any[]
  sorting: any[]
  columnVisibility: Record<string, boolean>
  columnOrder?: string[]
  globalFilter?: string
  pagination?: {
    pageSize: number
    pageIndex: number
  }
}

export interface TableView {
  id: number
  name: string
  tableIdentifier: string
  viewConfig: TableViewConfig
  scope: 'personal' | 'tenant'
  userId: number
  tenantId?: number
  eventId?: number
  isDefault: boolean
  lastModifiedBy?: number
  canEdit: boolean
  canDelete: boolean
  createdAt: string
  updatedAt: string
  ownerName?: string
}

export interface CreateTableViewParams {
  name: string
  tableIdentifier: string
  viewConfig: TableViewConfig
  scope: 'personal' | 'tenant'
  tenantId?: number
  eventId?: number
  isDefault?: boolean
}

export interface UpdateTableViewParams {
  name?: string
  viewConfig?: TableViewConfig
  isDefault?: boolean
}

// Query Keys
export const tableViewsQueryKey = 'common.tableViews';

// API Functions

// List all views for a specific table
export function loadTableViews(createRequest: () => ApiRequest) {
  const request = createRequest();
  return async (tableIdentifier?: string, tenantId?: number): Promise<ApiResponse<TableView>> => {
    request.method = 'GET';
    request.endpoint.path = '/api/v1/admin/table_views';
    request.endpoint.query = {};
    if (tableIdentifier) request.endpoint.query.table_identifier = tableIdentifier;
    if (tenantId) request.endpoint.query.tenant_id = tenantId;
    return await callApi<TableView>(request);
  };
}

// Get a specific view
export function getTableView(createRequest: () => ApiRequest) {
  const request = createRequest();
  return async (id: number): Promise<ApiResponse<TableView>> => {
    request.method = 'GET';
    request.endpoint.path = `/api/v1/admin/table_views/${id}`;
    return await callApi<TableView>(request);
  };
}

// Create a new view
export function createTableView(createRequest: () => ApiRequest) {
  const request = createRequest();
  return async (params: CreateTableViewParams): Promise<ApiResponse<TableView>> => {
    request.method = 'POST';
    request.endpoint.path = '/api/v1/admin/table_views';
    request.payload = { tableView: params };
    return await callApi<TableView>(request);
  };
}

// Update an existing view
export function updateTableView(createRequest: () => ApiRequest) {
  const request = createRequest();
  return async (id: number, params: UpdateTableViewParams): Promise<ApiResponse<TableView>> => {
    request.method = 'PUT';
    request.endpoint.path = `/api/v1/admin/table_views/${id}`;
    request.payload = { tableView: params };
    return await callApi<TableView>(request);
  };
}

// Delete a view
export function deleteTableView(createRequest: () => ApiRequest) {
  const request = createRequest();
  return async (id: number): Promise<void> => {
    request.method = 'DELETE';
    request.endpoint.path = `/api/v1/admin/table_views/${id}`;
    await deleteAPI(request);
  };
}

// Duplicate a view (Save As)
export function duplicateTableView(createRequest: () => ApiRequest) {
  const request = createRequest();
  return async (id: number, name: string, scope?: 'personal' | 'tenant'): Promise<ApiResponse<TableView>> => {
    request.method = 'POST';
    request.endpoint.path = `/api/v1/admin/table_views/${id}/duplicate`;
    const payload: any = { name };
    if (scope) payload.scope = scope;
    request.payload = payload;
    return await callApi<TableView>(request);
  };
}

// Set a view as default
export function setDefaultTableView(createRequest: () => ApiRequest) {
  const request = createRequest();
  return async (id: number): Promise<ApiResponse<TableView>> => {
    request.method = 'PUT';
    request.endpoint.path = `/api/v1/admin/table_views/${id}/set_default`;
    return await callApi<TableView>(request);
  };
}
